import { useState } from "react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MessageSquare, CheckCircle2 } from "lucide-react";

const empty = { name: "", email: "", phone: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message sent.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <div className="mx-auto max-w-6xl px-5 py-16 md:py-24" data-testid="contact-page">
        <div className="grid gap-12 md:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">
              Contact
            </p>
            <h1 className="mt-3 font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              Get in touch
            </h1>
            <p className="mt-6 leading-relaxed text-slate-600">
              Have a question about a registration or need help? Send us a
              message using the form and we'll get back to you.
            </p>
            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-white">
                  <MessageSquare className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-medium text-slate-900">Form-only inquiries</p>
                  <p className="text-sm text-slate-500">We respond to every message we receive.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-white">
                  <Mail className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-medium text-slate-900">Kept private</p>
                  <p className="text-sm text-slate-500">Your details are stored securely and never sold.</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            {done ? (
              <div className="rounded-md border border-slate-200 bg-white p-10 text-center shadow-sm" data-testid="contact-success">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h2 className="mt-6 font-display text-2xl font-bold tracking-tight text-slate-900">Message sent</h2>
                <p className="mt-3 text-slate-600">Thanks for reaching out. We'll be in touch soon.</p>
                <Button variant="outline" className="mt-6 rounded-md" onClick={() => { setForm(empty); setDone(false); }} data-testid="contact-reset-button">
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-md border border-slate-200 bg-white p-6 shadow-sm md:p-8" data-testid="contact-form">
                <div className="space-y-5">
                  <div>
                    <Label htmlFor="c-name" className="mb-1.5 block text-sm font-medium text-slate-700">Name</Label>
                    <Input id="c-name" data-testid="contact-input-name" required value={form.name} onChange={set("name")} placeholder="Your name" />
                  </div>
                  <div>
                    <Label htmlFor="c-email" className="mb-1.5 block text-sm font-medium text-slate-700">Email</Label>
                    <Input id="c-email" data-testid="contact-input-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
                  </div>
                  <div>
                    <Label htmlFor="c-phone" className="mb-1.5 block text-sm font-medium text-slate-700">Phone (optional)</Label>
                    <Input id="c-phone" data-testid="contact-input-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" />
                  </div>
                  <div>
                    <Label htmlFor="c-message" className="mb-1.5 block text-sm font-medium text-slate-700">Message</Label>
                    <Textarea id="c-message" data-testid="contact-input-message" required rows={5} value={form.message} onChange={set("message")} placeholder="How can we help?" />
                  </div>
                  <Button type="submit" data-testid="contact-submit-button" disabled={submitting} className="w-full rounded-md bg-blue-600 text-white hover:bg-blue-500">
                    {submitting ? "Sending…" : "Send Message"}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
